import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText, Shield, RotateCcw, HelpCircle } from "lucide-react";

const KIND_META = {
  privacy: { icon: Shield, label: "Privacy" },
  terms: { icon: FileText, label: "Terms" },
  refund: { icon: RotateCcw, label: "Refund" },
  faq: { icon: HelpCircle, label: "FAQ" },
};
const KINDS = ["privacy", "terms", "refund", "faq"];

export default function LegalPage() {
  const { kind } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/legal/${kind}`).then(r => setDoc(r.data)).catch(() => setDoc(null)).finally(() => setLoading(false));
  }, [kind]);

  if (!KINDS.includes(kind)) return <Redirect/>;

  return (
    <div className="min-h-screen bg-[#0c0d10] text-zinc-100">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0c0d10]/80 border-b border-zinc-800">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between gap-3">
          <button onClick={() => nav(-1)} className="text-zinc-400 hover:text-amber-400 flex items-center gap-1.5 text-sm" data-testid="legal-back">
            <ArrowLeft className="h-4 w-4"/>Back
          </button>
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400 font-bold">BULLER TAXI · LEGAL</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8 fade-in">
        <nav className="flex flex-wrap gap-2 mb-6" data-testid="legal-nav">
          {KINDS.map(k => {
            const M = KIND_META[k];
            const active = k === kind;
            return (
              <Link
                key={k}
                to={`/legal/${k}`}
                data-testid={`legal-link-${k}`}
                className={`px-3 py-1.5 rounded-full border text-xs font-semibold flex items-center gap-1.5 ${active ? "bg-amber-400 border-amber-400 text-zinc-950" : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-amber-400/40"}`}
              >
                <M.icon className="h-3 w-3"/>{M.label}
              </Link>
            );
          })}
        </nav>

        <Card className="bg-zinc-900 border-zinc-800 p-6 sm:p-8" data-testid={`legal-doc-${kind}`}>
          {loading ? (
            <div className="text-zinc-500">Loading…</div>
          ) : doc ? (
            <article>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-2" data-testid="legal-title">{doc.title}</h1>
              {doc.updated_at && (
                <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-6">Last updated · {new Date(doc.updated_at).toLocaleDateString()}</p>
              )}
              <div className="text-zinc-300 leading-relaxed whitespace-pre-wrap text-[15px]" data-testid="legal-content">
                {doc.content}
              </div>
            </article>
          ) : (
            <div className="text-zinc-500">Document not found.</div>
          )}
        </Card>

        <div className="mt-6 text-center text-[11px] text-zinc-500">
          Questions? Call <a href="tel:+15802194687" className="text-amber-400 hover:text-amber-300 font-bold">(580) 219-4687</a>
        </div>
        <div className="mt-3 text-center">
          <Button variant="outline" asChild className="bg-zinc-950 border-zinc-700"><Link to="/login">Return to Login</Link></Button>
        </div>
      </main>
    </div>
  );
}

function Redirect() {
  if (typeof window !== "undefined") window.location.href = "/legal/privacy";
  return null;
}
